import z from "zod";

import { TABS_VARIANTS } from "./constants";

export const tabsDataSchema = z.object({
  defaultValue: z
    .string()
    .optional()
    .describe(
      "Initial active tab. Read once, at mount — use `value` instead to change tabs after the initial render.",
    ),
  value: z
    .string()
    .optional()
    .describe(
      "Current active tab. Settable at any time — e.g. `el.setAttribute('value', 'tab2')` or `el.setValue('tab2')` — to switch tabs externally.",
    ),
  tabs: z
    .array(
      z.object({
        value: z.string().describe("Unique identifier for this tab."),
        label: z.string().describe("Visible label shown on the tab."),
        icon: z
          .string()
          .optional()
          .describe("Optional icon name shown next to the label."),
      }),
    )
    .default([])
    .describe(
      "Tabs parsed from the light DOM (<rwc-tabs-tab> elements inside <rwc-tabs-list>) — not a Twig-authored attribute.",
    ),
  hasActions: z
    .boolean()
    .optional()
    .describe(
      "Set by readTabsSlotted when the light DOM carries an <rwc-tabs-actions> — not a Twig-authored attribute.",
    ),
  variant: z.enum(TABS_VARIANTS).optional(),
  scrollable: z
    .string()
    .optional()
    .transform((value) => value !== undefined && value !== "false")
    .describe(
      "Fills the host's height: the tab list stays put and the panels scroll. Give the host a height from the page.",
    ),
  alerts: z
    .string()
    .optional()
    .transform((value) =>
      value === undefined
        ? []
        : value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
    )
    .describe(
      "Comma-separated values of the tabs flagged with a warning icon, e.g. something left to fill in that panel. Settable at any time.",
    ),
  onValueChange: z
    .custom<(value: string) => void>()
    .optional()
    .describe(
      "Internal callback bound by readTabsSlotted — not a Twig-authored attribute, passed through the same raw-props pipeline.",
    ),
});

export type TabsDataProps = z.infer<typeof tabsDataSchema>;
